import { cn } from '@src/design-system/helpers';
import { inputVariants } from '@src/design-system/input';
import { ChevronDownIcon } from 'lucide-react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverClose,
} from '@src/design-system/popover';
import { InfiniteList } from '@src/design-system/molecules/infinite-list';
import viewApi from '../services/view-api';
import { ErrorMessage, useFormikContext } from 'formik';
import { CustomErrorMessage } from '@src/design-system/error-message';
import { Label } from '@src/design-system/label';

import type { TView } from '@shared/types/view';
import type { TBulkReminderFormValues } from '@src/features/participants/components/modals/send-bulk-reminder-to-student-modal/send-bulk-reminder.schema';

export type SavedViewsSelectProps = {
  entity_type: TView['entity_type'];
  programId?: number;
  appliedAll?: Partial<Record<TView['entity_type'], string>>;
};

export const SavedViewsSelect = ({
  entity_type,
  programId,
  appliedAll,
}: SavedViewsSelectProps) => {
  const { setFieldValue, values } =
    useFormikContext<Partial<TBulkReminderFormValues>>();

  const allOptionLabel = appliedAll?.[entity_type];
  const view = values?.view as TView | 'all' | undefined;

  const isAllOptionSelected = !!allOptionLabel && view === 'all';

  const selectedViewName =
    view && typeof view === 'object' && 'name' in view ? view.name : undefined;

  const triggerLabel = isAllOptionSelected
    ? allOptionLabel
    : selectedViewName || 'Saved views';

  return (
    <div className='my-2'>
      <Label>Select Saved View</Label>
      <Popover modal>
        <PopoverTrigger
          className={cn(
            inputVariants(),
            'w-full flex justify-between items-center text-xs gap-2'
          )}
        >
          <span className='flex-1 truncate text-left'>{triggerLabel}</span>
          <ChevronDownIcon
            size={20}
            className='flex-shrink-0'
          />
        </PopoverTrigger>
        <PopoverContent
          withPortal={true}
          style={{ width: 'var(--radix-popover-trigger-width)' }}
          align='start'
        >
          <InfiniteList
            fetchInfiniteData={viewApi.useFetchInfinite}
            query={{ entity_type, entity_id: programId }}
            searchParamKey='name'
            animateOnEveryRender
            props={{
              listProps: { className: 'gap-0' },
              listContainerProps: { className: 'max-h-[300px]' },
            }}
            enableSearch
            searchParamName='view'
            allItem={
              allOptionLabel ? (
                <PopoverClose
                  onClick={() =>
                    setFieldValue(
                      'view',
                      'all' as unknown as TBulkReminderFormValues['view']
                    )
                  }
                  className={cn(
                    'p-2 text-left hover:bg-primary-30 cursor-pointer text-sm inline-block w-full',
                    isAllOptionSelected && 'bg-primary-100 hover:bg-primary-100'
                  )}
                >
                  {allOptionLabel}
                </PopoverClose>
              ) : undefined
            }
            renderItem={(p) => {
              const isSelected =
                typeof view === 'object' && view != null && view.id === p.id;
              return (
                <PopoverClose
                  onClick={() => {
                    setFieldValue('view', p);
                  }}
                  className={cn(
                    'p-2 text-left hover:bg-primary-30 cursor-pointer text-sm inline-block w-full',
                    isSelected && 'bg-primary-100 hover:bg-primary-100'
                  )}
                >
                  {p.name}
                </PopoverClose>
              );
            }}
          />
        </PopoverContent>
      </Popover>
      <ErrorMessage
        name='view'
        component={CustomErrorMessage}
      />
    </div>
  );
};
